/**
 * Shared, client-safe vocabulary for the grievance (complaint) case system.
 *
 * The DCOG 2021 §9 loop is: a complaint is received, acknowledged within 48
 * hours, worked, and resolved within 15 working days. Statuses below mirror
 * that ladder so the buyer-facing timeline and the admin queue can never
 * disagree about what a case state means.
 */

export const GRIEVANCE_STATUSES = [
  "received",
  "acknowledged",
  "under_review",
  "awaiting_reply",
  "resolved",
  "closed",
] as const;

export type GrievanceStatus = (typeof GRIEVANCE_STATUSES)[number];

export const GRIEVANCE_CATEGORIES = [
  "order",
  "refund",
  "delivery",
  "vendor_conduct",
  "product_safety",
  "privacy",
  "payment",
  "other",
] as const;

export type GrievanceCategory = (typeof GRIEVANCE_CATEGORIES)[number];

export const STATUS_LABEL: Record<GrievanceStatus, { en: string; bn: string }> = {
  received: { en: "Received", bn: "গৃহীত" },
  acknowledged: { en: "Acknowledged", bn: "প্রাপ্তি স্বীকৃত" },
  under_review: { en: "Under review", bn: "পর্যালোচনাধীন" },
  awaiting_reply: { en: "Awaiting your reply", bn: "আপনার উত্তরের অপেক্ষায়" },
  resolved: { en: "Resolved", bn: "সমাধান হয়েছে" },
  closed: { en: "Closed", bn: "বন্ধ" },
};

export const CATEGORY_LABEL: Record<GrievanceCategory, { en: string; bn: string }> = {
  order: { en: "Order problem", bn: "অর্ডার সমস্যা" },
  refund: { en: "Refund or return", bn: "রিফান্ড বা রিটার্ন" },
  delivery: { en: "Delivery or courier", bn: "ডেলিভারি বা কুরিয়ার" },
  vendor_conduct: { en: "Seller conduct", bn: "বিক্রেতার আচরণ" },
  product_safety: { en: "Counterfeit or unsafe part", bn: "নকল বা অনিরাপদ যন্ত্রাংশ" },
  privacy: { en: "Privacy or data", bn: "গোপনীয়তা বা ডেটা" },
  payment: { en: "Payment problem", bn: "পেমেন্ট সমস্যা" },
  other: { en: "Something else", bn: "অন্য কিছু" },
};

/** Statuses where the case is still live and the complainant can reply. */
export const OPEN_STATUSES: readonly GrievanceStatus[] = [
  "received",
  "acknowledged",
  "under_review",
  "awaiting_reply",
];

export function isOpenStatus(s: string): boolean {
  return (OPEN_STATUSES as readonly string[]).includes(s);
}

export function statusTone(s: string): "info" | "warn" | "ok" | "muted" {
  if (s === "resolved") return "ok";
  if (s === "closed") return "muted";
  if (s === "awaiting_reply") return "warn";
  return "info";
}

/** `GRV-2608-0417` — month-stamped so support can eyeball case age. */
export function looksLikeReference(v: string): boolean {
  return /^GRV-\d{4}-\d{4}$/i.test(v.trim());
}

export function normalizeReference(v: string): string {
  return v.trim().toUpperCase();
}

export type GrievanceEvent = {
  id: string;
  kind: string;
  from_status: string | null;
  to_status: string | null;
  body: string | null;
  actor_role: string;
  actor_label: string | null;
  created_at: string;
};

export type GrievanceCase = {
  id: string;
  reference: string;
  name: string;
  email: string;
  phone: string | null;
  category: string;
  order_number: string | null;
  subject: string;
  body: string;
  status: string;
  ack_deadline: string;
  resolve_deadline: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  created_at: string;
  attachments?: { path: string; name: string }[];
};
